/**
 * Converts a date + charge time (in a given timezone) to a UTC ISO string.
 *
 * Used by UpdatePayment and CreateFundraisingPaymentDrawer to build
 * timezone-aware scheduled payment timestamps.
 *
 * @param {Date|string|null} date     - The target date
 * @param {string|null}      chargeTime - "HH:MM", "now", or null
 * @param {string}           timezone   - IANA timezone (e.g. "America/New_York")
 * @returns {string|Date|null} UTC ISO string, or the original value if inputs are falsy
 */
export const applyChargeTimeToDate = (date, chargeTime, timezone) => {
  if (!date || !chargeTime) return date;

  try {
    let hours;
    let minutes;

    if (chargeTime === 'now') {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
      });
      const timeParts = formatter.formatToParts(now);
      const currentHour = parseInt(timeParts.find((p) => p.type === 'hour')?.value || '0', 10);
      const currentMinute = parseInt(timeParts.find((p) => p.type === 'minute')?.value || '0', 10);
      hours = currentMinute > 0 ? currentHour + 1 : currentHour;
      minutes = 0;
      if (hours >= 24) {
        hours = 0;
      }
    } else {
      [hours, minutes] = chargeTime.split(':').map(Number);
    }

    const targetDate = new Date(date);

    // Round up to next hour if there are any minutes
    let roundedHours = hours;
    let dayOffset = 0;
    if (minutes > 0) {
      roundedHours = hours + 1;
      if (roundedHours >= 24) {
        roundedHours = 0;
        dayOffset = 1;
      }
    }

    const year = targetDate.getFullYear();
    const day = targetDate.getDate() + dayOffset;

    // Handle month overflow from day offset
    const tempDate = new Date(year, targetDate.getMonth(), day);
    const finalYear = tempDate.getFullYear();
    const finalMonth = String(tempDate.getMonth() + 1).padStart(2, '0');
    const finalDay = String(tempDate.getDate()).padStart(2, '0');

    // Create a date string representing the time in the given timezone
    const dateTimeString = `${finalYear}-${finalMonth}-${finalDay}T${String(roundedHours).padStart(2, '0')}:00:00`;

    // Get timezone offset for conversion to UTC
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    });
    const testDate = new Date(dateTimeString);
    const parts = formatter.formatToParts(testDate);
    const offsetPart = parts.find((p) => p.type === 'timeZoneName');

    if (offsetPart) {
      const offsetMatch = offsetPart.value.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
      if (offsetMatch) {
        const sign = offsetMatch[1];
        const offsetHours = offsetMatch[2].padStart(2, '0');
        const offsetMinutes = offsetMatch[3] || '00';
        const isoString = `${dateTimeString}${sign}${offsetHours}:${offsetMinutes}`;
        return new Date(isoString).toISOString();
      }
    }

    // Fallback: use local time conversion
    targetDate.setDate(targetDate.getDate() + dayOffset);
    targetDate.setHours(roundedHours, 0, 0, 0);
    return targetDate.toISOString();
  } catch (error) {
    console.error('Error applying charge time to date:', error);
    return date instanceof Date ? date.toISOString() : date;
  }
};
